import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { facebookApi } from "./client";

export const facebookKeys = {
  all: ["facebook"] as const,
  setup: () => ["facebook", "setup"] as const,
  pages: () => ["facebook", "pages"] as const,
  webhookActivity: (params: { limit?: number; facebookPageId?: string } = {}) =>
    ["facebook", "webhook-activity", params] as const,
};

export function useFacebookSetupQuery() {
  return useQuery({
    queryKey: facebookKeys.setup(),
    queryFn: () => facebookApi.setup(),
    staleTime: 60_000,
  });
}

export function useFacebookPagesQuery() {
  return useQuery({
    queryKey: facebookKeys.pages(),
    queryFn: () => facebookApi.listPages(),
  });
}

export function useConnectFacebookPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pageId: string) => facebookApi.connectPage(pageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: facebookKeys.all });
      toast.success("ເຊື່ອມ webhook ສໍາເລັດ");
    },
  });
}

export function useDisconnectFacebookPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pageId: string) => facebookApi.disconnectPage(pageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: facebookKeys.all });
      toast.success("ຕັດການເຊື່ອມ Page ສໍາເລັດ");
    },
    onError: () => toast.error("ຕັດການເຊື່ອມ Page ລົ້ມເຫຼວ"),
  });
}

export function useCheckFacebookPageToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pageId: string) => facebookApi.tokenStatus(pageId),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: facebookKeys.all });
      if (res.status.isValid) {
        toast.success("Token ຍັງໃຊ້ງານໄດ້");
      } else {
        toast.error("Token ຫຼື ສິດ Facebook ບໍ່ຄົບ", {
          description:
            res.status.missingScopes.length > 0
              ? `ຂາດ: ${res.status.missingScopes.join(", ")}`
              : "ກະລຸນາເຊື່ອມ Facebook ໃໝ່",
        });
      }
    },
    onError: () => toast.error("ກວດສອບ token ລົ້ມເຫຼວ"),
  });
}

export function useWebhookActivityQuery(
  params: { limit?: number; facebookPageId?: string; enabled?: boolean } = {},
) {
  const { enabled = true, ...rest } = params;
  return useQuery({
    queryKey: facebookKeys.webhookActivity(rest),
    queryFn: () => facebookApi.webhookActivity(rest),
    enabled,
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}

export function useRefreshFacebookTokens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => facebookApi.refreshTokens(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: facebookKeys.all });
      const { refreshed, requiresReconnect } = res.result;
      if (refreshed > 0 && requiresReconnect.length === 0) {
        toast.success("ຣີເຟຣສ Token ສຳເລັດ", {
          description: `ອັບເດດ ${refreshed} Page`,
        });
      } else if (refreshed > 0) {
        toast.success("ຣີເຟຣສ Token ບາງສ່ວນ", {
          description: `ສຳເລັດ ${refreshed} · ຕ້ອງເຊື່ອມໃໝ່ ${requiresReconnect.length}`,
        });
      } else if (requiresReconnect.length > 0) {
        toast.error("ຣີເຟຣສ Token ບໍ່ສຳເລັດ", {
          description: "ກະລຸນາກົດ «ເຊື່ອມຕໍ່ Facebook ໃໝ່»",
        });
      } else {
        toast.success("Token ທັງໝົດຍັງໃຊ້ງານໄດ້");
      }
    },
    onError: () => toast.error("ຣີເຟຣສ Token ລົ້ມເຫຼວ"),
  });
}
